import React, {
    useContext,
    useEffect,
    useMemo,
    useState,
    useCallback,
} from 'react'
import {
    ComposedChart,
    Line,
    YAxis,
    XAxis,
    Tooltip,
    Label,
    CartesianGrid,
    ResponsiveContainer,
    ReferenceArea,
    Legend,
} from 'recharts'

import { DataContext } from './DataContext'
import Message from '../../../elem/chart/Message'
import Spinner from '../../../elem/Spinner'
import ChartTooltip from '../../../elem/chart/Tooltip'
import { dateToString } from '../../../../utils/dateUtils'
import { AppStateContext } from '../AppStateContext'
import withConfig from '../../../wrappers/withConfig'
import dayjs from 'dayjs'
import palette from '../../../../utils/chart/palette'
import { getAxisYDomain } from '../../../../utils/chart/slice'
import { roundToFourDigits } from '../../../../utils/chart/values'
import { PanelStyleContext } from '../../../elem/panel/PanelStyleContext'

// Helper function (define outside the component)
const hasValidChartData = (paramChartData) => {
    return (
        paramChartData &&
        Array.isArray(paramChartData) &&
        paramChartData.length &&
        paramChartData.every(
            (e) => e.Data && Array.isArray(e.Data) && e.Data.length
        )
    )
}

const CustomLegend = ({ legendPayload }) => {
    return (
        <ul className="recharts-default-legend" style={{ textAlign: 'center' }}>
            {legendPayload.map((item, index) => (
                <li
                    key={`line-legend-${index}`}
                    className={`recharts-legend-item legend-item-${index}`}
                    style={{ display: 'inline-block', marginRight: '10px' }}
                >
                    <svg
                        className="recharts-surface"
                        width="14"
                        height="14"
                        viewBox="0 0 32 32"
                        version="1.1"
                        style={{
                            display: 'inline-block',
                            verticalAlign: 'middle',
                            marginRight: '4px',
                        }}
                    >
                        <path
                            stroke="none"
                            fill={item.color}
                            d="M0,4h32v24h-32z"
                            className="recharts-legend-icon"
                        ></path>
                    </svg>
                    <span className="recharts-legend-item-text">
                        {item.value}
                    </span>
                </li>
            ))}
        </ul>
    )
}

const AggregateChart = withConfig(({ config, headerHeight, name }) => {
    const {
        paramChartData,
        loading,
        tooManyFeatures,
        selectedUnits,
        selectedParams,
        zoomTrigger,
        isCollapsed,
        setChartVisibleRange,
    } = useContext(DataContext)
    const {
        mapState: { selectedFeatures },
    } = useContext(AppStateContext)
    const { isExpanded } = useContext(PanelStyleContext)

    const { FACILITY_TITLE } = config

    const [data, setData] = useState([])

    const [left, setLeft] = useState('dataMin')
    const [right, setRight] = useState('dataMax')
    const [refAreaLeft, setRefAreaLeft] = useState('')
    const [refAreaRight, setRefAreaRight] = useState('')
    const [top, setTop] = useState('dataMax')
    const [bottom, setBottom] = useState('dataMin')

    const seriesData = useMemo(() => {
        if (paramChartData && paramChartData.length) {
            const dates = [
                ...new Set(
                    paramChartData.reduce((acc, curr) => {
                        return [...acc, ...curr.Data.map((x) => x.DateString)]
                    }, [])
                ),
            ]
            const d = dates.map((date) => {
                const dateEntry = { DateString: date }
                const allDateData = paramChartData.reduce((acc, curr) => {
                    const paramID = curr.ParamID
                    const paramData = curr.Data
                    const matchDate = paramData.find(
                        (x) => x.DateString === date
                    )
                    const associatedParam = selectedParams.find(
                        (x) => x.ParamID === paramID
                    )
                    if (matchDate && associatedParam) {
                        const paramAlias = associatedParam.ParamAlias
                        const minName = `${paramAlias} (min)`
                        const maxName = `${paramAlias} (max)`
                        const medianName = `${paramAlias} (median)`
                        const numSamplesName = `${paramAlias} (# samples)`
                        return {
                            ...acc,
                            [minName]: matchDate['Minimum'],
                            [maxName]: matchDate['Maximum'],
                            [medianName]: matchDate['Median'],
                            [numSamplesName]: matchDate['NumberOfSamples'],
                        }
                    } else {
                        return {
                            ...acc,
                        }
                    }
                }, dateEntry)
                return allDateData
            })
            return d
        } else {
            return []
        }
    }, [paramChartData, selectedParams])

    useEffect(() => {
        if (seriesData && seriesData.length) {
            const d = seriesData.map((x) => ({
                ...x,
                DateString: dayjs(x.DateString).toDate().getTime(),
            }))
            setData(d)
            // Reset only y axis domain when new data is set to ensure proper y axis max min domain when zoomed into the chart
            setTop('dataMax')
            setBottom('dataMin')
        }
    }, [seriesData])

    const uniqueSeries = useMemo(
        () =>
            [
                ...new Set(
                    seriesData.reduce(
                        (acc, curr) => [
                            ...acc,
                            ...Object.keys(curr).filter(
                                (x) => x !== 'DateString'
                            ),
                        ],
                        []
                    )
                ),
            ]
                .sort()
                .map((x, idx) => ({
                    name: x,
                    color: palette[idx % palette.length],
                })),
        [seriesData]
    )

    const legendData = useMemo(
        () =>
            uniqueSeries &&
            uniqueSeries
                .filter(({ name }) => !name.includes('# samples'))
                .map(({ name, color }) => ({
                    color,
                    type: 'line',
                    dataKey: name,
                    value: name,
                })),
        [uniqueSeries]
    )

    const zoom = useCallback(() => {
        let RAL = refAreaLeft
        let RAR = refAreaRight
        if (RAL === RAR || RAR === '') {
            RAL = ''
            RAL = ''
            return
        }

        // xAxis domain
        if (RAL > RAR) {
            const temp = RAL
            RAL = RAR
            RAR = temp
        }

        // yAxis domain
        const leftAxisDomain = getAxisYDomain(
            RAL,
            RAR,
            (x) => !x.includes('# samples') && !x.includes('DateString'),
            0,
            data,
            'DateString'
        )
        setBottom(leftAxisDomain[0])
        setTop(leftAxisDomain[1])
        setLeft(RAL)
        setRight(RAR)
        setRefAreaRight('')
        setRefAreaLeft('')
    }, [refAreaLeft, refAreaRight, data])

    const zoomOut = useCallback(() => {
        setRefAreaLeft('')
        setRefAreaRight('')
        setLeft('dataMin')
        setRight('dataMax')
        setTop('dataMax')
        setBottom('dataMin')
    }, [data])

    useEffect(() => {
        zoomOut()
    }, [zoomTrigger, paramChartData])

    useEffect(() => {
        setChartVisibleRange({ left, right })
    }, [left, right, setChartVisibleRange])

    // construct chart
    const concentrationAxisLabel = `Result Value (${selectedUnits})`
    const xAxisLabel = 'Sample Date'

    // create lines for min, max, and median levels
    // "# samples" lines are kept invisible (no dots) but included for tooltip display
    const lines = uniqueSeries.map(({ name, color }, idx) => {
        const isSamplesCount = name.includes('# samples')
        return (
            <Line
                key={`time-series-${name}`}
                yAxisId={0}
                type="monotone"
                dataKey={name}
                name={name}
                unit={isSamplesCount ? '' : selectedUnits}
                stroke={color}
                strokeWidth={0}
                activeDot={
                    isSamplesCount
                        ? false
                        : { r: 5, fill: color, stroke: color }
                }
                dot={
                    isSamplesCount
                        ? false
                        : { clipDot: false, r: 3, stroke: color, fill: color }
                }
                isAnimationActive={false}
            />
        )
    })

    return (
        <div className={`${isCollapsed ? 'is-collapsed' : ''}`}>
            {loading ? (
                <Spinner extraClass="chartWrapper" />
            ) : tooManyFeatures ? (
                <Message
                    text={`Too Many ${FACILITY_TITLE}s Selected`}
                    subText={`Select less than 2100 ${FACILITY_TITLE.toLowerCase()}s to generate aggregate statistics`}
                />
            ) : !selectedFeatures.length ? (
                <Message
                    text={`No ${FACILITY_TITLE}s Selected`}
                    subText={`Select up to 2100 ${FACILITY_TITLE.toLowerCase()}s to generate aggregate statistics`}
                />
            ) : !hasValidChartData(paramChartData) ? (
                <Message
                    text={'No Parameter Data Available'}
                    subText={`Select ${FACILITY_TITLE.toLowerCase()}s with samples of the selected parameters in the specified time period to generate aggregate statistics`}
                />
            ) : (
                <div className={`aggregateChartWrapper`}>
                    <div
                        style={{
                            height: isExpanded
                                ? `calc(100% - ${headerHeight}px)`
                                : '100%',
                        }}
                    >
                        <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart
                                data={data.slice()}
                                margin={{
                                    top: 10,
                                    right: 20,
                                    left: 25,
                                    bottom: 25,
                                }}
                                onMouseDown={(e) =>
                                    e &&
                                    e.activeLabel &&
                                    setRefAreaLeft(e.activeLabel)
                                }
                                onMouseMove={(e) =>
                                    e &&
                                    e.activeLabel &&
                                    refAreaLeft &&
                                    setRefAreaRight(e.activeLabel)
                                }
                                // eslint-disable-next-line react/jsx-no-bind
                                onMouseUp={() => zoom()}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    allowDataOverflow
                                    type="number"
                                    dataKey="DateString"
                                    domain={[left, right]}
                                    tickFormatter={(unixTime) =>
                                        dateToString(unixTime)
                                    }
                                >
                                    <Label
                                        value={xAxisLabel}
                                        offset={-10}
                                        position="insideBottom"
                                        className="nitrateChartXAxisLabel"
                                    />
                                </XAxis>
                                <YAxis
                                    allowDataOverflow
                                    type="number"
                                    domain={[bottom, top]}
                                    tickFormatter={(v) => roundToFourDigits(v)}
                                    yAxisId={0}
                                >
                                    <Label
                                        value={concentrationAxisLabel}
                                        angle={-90}
                                        offset={20}
                                        position="insideLeft"
                                        className="nitrateChartYAxisLabel"
                                    />
                                </YAxis>
                                <Tooltip
                                    content={<ChartTooltip stagger={90} />}
                                    offset={{ x: 15, y: 10 }}
                                    allowEscapeViewBox={{ y: true }}
                                />
                                <Legend
                                    content={
                                        <CustomLegend
                                            legendPayload={legendData}
                                        />
                                    }
                                    verticalAlign="bottom"
                                    wrapperStyle={{
                                        bottom: `0`,
                                        left: 0,
                                        alignItems: 'center',
                                        width: '100%',
                                    }}
                                />
                                {lines}
                                {/* {dots} */}
                                {refAreaLeft && refAreaRight ? (
                                    <ReferenceArea
                                        yAxisId={0}
                                        x1={refAreaLeft}
                                        x2={refAreaRight}
                                        strokeOpacity={0.3}
                                    />
                                ) : null}
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}
        </div>
    )
})

export default AggregateChart
